import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// import LoaderIcon from '@/components/Common/LoaderIcon';
import { resetCatalogsList } from '../../../../../../redux/actions';
import { getProducts } from '../../../MiddleBody5/utils';
import {
  CategoriesItemsContainer,
  CategoriesWrapper,
  CategoryListWrapper,
  CategoryName,
} from './AllCategoriesItems.styles';
import { useSSRSelector } from '@/redux/ssrStore';
import { ProductListWrapper } from 'src/components/DesignSystem/Widgets2.0/ProductListWrapper';
import useOnScreen from 'src/hooks/useOnScreen';
import { getThemeColor } from 'src/utils/getThemeColor';
import PaginationComponent from 'src/components/DesignSystem/Widgets2.0/SortAndFilter/Pagination';
import { useWidgetDndContextData } from 'src/context/WidgetDndContext';
import { isMobileDevice } from 'src/components/WidgetMaker/WidgetDnD/isMobileDevice';
import { PAGINATION } from 'src/components/DesignSystem/Widgets2.0/SortAndFilter/constant';
import { getPaginatedItemsPerPage } from '../../utils';
import { CATEGORY_TYPE } from 'src/utils/constants';

function AllCategoriesItems(props) {
  const dispatch = useDispatch();
  const { comingFromAdvCatlog } = props;
  const {
    widgetContextState: { pageStyle, isBuilder },
  } = useWidgetDndContextData();
  const { gridAndPagination = {} } = pageStyle || {};
  const { catalog_items, totalPages, sortType } = useSelector((state: any) => ({
    catalog_items: state.catalogReducer.catalog_items,
    totalPages: state.catalogReducer?.totalPages,
    sortType: state.catalogReducer?.sortType,
  }));
  const {
    storeData: { store_id },
    categories,
    productTags,
  } = useSSRSelector((state) => ({
    storeData: state.storeReducer.store,
    categories: state.catalogReducer.categories,
    productTags: state.catalogReducer?.productTags ?? [],
  }));
  const isMobile = isMobileDevice();
  /**
   * the following data has been kept in useRef for the purpose of
   * synchronous data manipulation
   */

  /**
   * categoryPaginationStatus : it holds data for each category if its nextPage
   * is there and array for page number for which the api has been called
   */
  const categoryPaginationStatus = useRef({});

  /**
   * categoryPaginationIterator : its an Iterator to change category nextPage To true
   * when the one category is full rendered and we need to render the next category
   */
  // const categoryPaginationIterator = useRef(0);

  /**
   * categoryIdArray : contains categoryId's
   */
  const categoryIdArray = useRef([]);

  /**
   * loader : used to control pagination api. if one api is called till its
   * execution is not done loader will not change state and critical section of code
   * is not invoked
   */
  const loader = useRef({});

  /** for new PLP flow we only show single category at a time. Adding a check here
   *  if it's coming from advance catalog we will render only single category because in new PLP flow 
   *  we replaced all the PLP component with advance catalog component */
  
  const updatedCategoryData = useMemo(() => comingFromAdvCatlog ? getSelectedCategory() : categories, [props?.selectedId, comingFromAdvCatlog]);

  function getSelectedCategory() {
      return [...productTags, ...categories]?.filter((data) => Number(data.id) === Number(props?.selectedId))
  }

  /**
   * initializing categoryIdArray and categoryPaginationStatus
   */
  useEffect(() => {
    const tempCategoryPaginationStatus = {};
    updatedCategoryData &&
      !!updatedCategoryData?.length &&
      updatedCategoryData.forEach((item, index) => {
        const tempStatusObj = {};
        tempStatusObj['isNext'] = index === 0 ? true : false;
        tempStatusObj['pages'] = [0];
        tempCategoryPaginationStatus[item.id] = tempStatusObj;
      });
    categoryPaginationStatus.current = tempCategoryPaginationStatus;
    updatedCategoryData &&
      !!updatedCategoryData?.length &&
      updatedCategoryData.forEach((item) => {
        categoryIdArray.current.push(item?.id);
      });

    return () => {
      dispatch(resetCatalogsList());
    };
  }, [updatedCategoryData]);

  /**
   * @param {number} categoryId
   * @return {number} page number for api.
   */
  function categoryPageNumber(categoryId) {
    return (
      categoryPaginationStatus.current[categoryId]?.pages?.[
      categoryPaginationStatus.current[categoryId]?.pages?.length - 1
      ] + 1
    );
  }

  const isTag = (categoryId) =>
    productTags?.length && productTags.findIndex((tag) => tag.id === categoryId) >= 0;

  async function fetchProductsForCategory(categoryId, pageNo, storePrevitem = true) {
    if (!store_id) return;

    return await getProducts(
      categoryId,
      isTag(categoryId) ? CATEGORY_TYPE.TAG : CATEGORY_TYPE.CATEGORY,
      pageNo,
      store_id,
      sortType,
      storePrevitem,
      getPaginatedItemsPerPage(gridAndPagination, isMobile)
    );
  }

  /**
   * loads data / is invoked when user Scroll end
   * @param {number} categoryId
   */
  async function loadMoreDataCategories(categoryId) {
    const pageNo = categoryPageNumber(categoryId);
    /** if loader is not there isNExt for category is true and pageNumber api is never called then call api */
    if (
      !loader.current?.[categoryId] &&
      !categoryPaginationStatus.current[categoryId]?.pages?.includes(pageNo) && categoryId
    ) {
      loader.current[categoryId] = true;
      if (!store_id) return;
      const { status, isNextPage } = await fetchProductsForCategory(categoryId, pageNo, true);
      if (status) {
        categoryPaginationStatus.current[categoryId]?.pages?.push(pageNo);
        categoryPaginationStatus.current[categoryId]['isNext'] = isNextPage;
      }
    }
    loader.current[categoryId] = false;
  }

  async function loadMorePaginatedData(categoryId, pageNo) {
    await fetchProductsForCategory(categoryId, pageNo, false);
  }

  /**
   *
   * @param {number} categoryId
   * @return {object} category data that needs to be rendered.
   */

  function returnNestedCategoryData(categoryId) {
    let categoryIdValue = undefined;
    categoryIdValue = catalog_items[categoryId];
    return categoryIdValue;
  }

  return (
    <CategoriesWrapper
      showFilterDrawer={props?.showFilterDrawer}
      showSortByDropDown={props?.showSortByDropDown}
    >
      {updatedCategoryData?.map((category) => (
        <CategorySection
          key={category.id}
          category={category}
          loadMoreDataCategories={loadMoreDataCategories}
          loadMorePaginatedData={loadMorePaginatedData}
          returnNestedCategoryData={returnNestedCategoryData}
          categoryPaginationStatus={categoryPaginationStatus}
          loader={loader}
          comingFromAdvCatlog={comingFromAdvCatlog}
          catalog_items={catalog_items}
          totalPages={totalPages}
          isBuilder={isBuilder}
        />
      ))}
    </CategoriesWrapper>
  );
}

export default AllCategoriesItems;

const CategorySection = ({
  category,
  loadMoreDataCategories,
  loadMorePaginatedData,
  returnNestedCategoryData,
  categoryPaginationStatus,
  loader,
  catalog_items,
  comingFromAdvCatlog,
  totalPages,
  isBuilder,
}) => {
  const {
    widgetContextState: { pageStyle },
  } = useWidgetDndContextData();
  const { gridAndPagination = {} } = pageStyle || {};
  const themeColor = getThemeColor();
  const ref = useRef(null);
  const scrollRef = useRef(null);
  const hasLoaded = useRef(false);

  const isVisible = useOnScreen(ref, { threshold: 0.1 });
  const isMobile = isMobileDevice();
  const isNumberedPagination = checkIfNumberedPagination();

  useEffect(() => {
    if (
      !isNumberedPagination &&
      isVisible &&
      !hasLoaded.current &&
      !catalog_items?.[category.id]
    ) {
      loadMoreDataCategories(category.id);
      hasLoaded.current = true;
    }
  }, [isVisible, catalog_items]);

  useEffect(() => {
    // If the pagination type is not infinite scroll, then fetch the category data for selected category with page number 1
    if (isNumberedPagination) {
      loadMorePaginatedData(category.id, 1);
    }
  }, [isNumberedPagination]);

  /**
   * @description Checks whether the pagination type is Numbered pagination ( as compared to the default infinite scroll )
   * @returns boolean
   */
  function checkIfNumberedPagination() {
    const { pagination_style_desktop = '', pagination_style_mobile = '' } =
      gridAndPagination || {};
    const style = isMobile ? pagination_style_mobile : pagination_style_desktop;
    return style === PAGINATION;
  }

  const scrollToDiv = () => {
    if (isBuilder){
      scrollRef?.current?.scrollIntoView({ behaviour: 'smooth', block: 'start' });
    } else {
      const { top } = scrollRef?.current?.getBoundingClientRect();
      window.scrollTo({ top: top + window.scrollY - 200, behavior: 'smooth' });
    }
  };

  return (
    <div ref={scrollRef}>
      <CategoryListWrapper ref={ref} key={category?.id}>
        {!comingFromAdvCatlog && <CategoryName>{category?.name}</CategoryName>}
        <CategoriesItemsContainer>
          <ProductListWrapper
            productList={returnNestedCategoryData(category?.id)}
            calculateSelfDimensions
            renderAdvancedCard
            oldCardConfigs={{
              showButton: true,
              noTagsReadState: 'fromLength',
            }}
            customClass="tw-mt-[24px]"
          />
          {isNumberedPagination ? (
            <div className="tw-my-[50px]">
              <PaginationComponent
                totalPages={totalPages}
                loadSelectedPageData={(pageNo) => {
                  scrollToDiv();
                  loadMorePaginatedData(category?.id, pageNo);
                }}
              />
            </div>
          ) : (
            categoryPaginationStatus.current[category?.id]?.isNext &&
            !loader?.current?.[category?.id] && (
              <div className="tw-flex tw-w-full tw-justify-center">
                <div
                  onClick={() => loadMoreDataCategories(category?.id)}
                  style={{ borderColor: themeColor, color: themeColor }}
                  className={
                    'tw-mx-8 tw-mb-34 tw-mt-24 tw-w-[100px] tw-cursor-pointer tw-border tw-border-solid tw-py-12 tw-text-center tw-text-14 tw-font-medium sm:tw-w-full'
                  }
                >
                  View More
                </div>
              </div>
            )
          )}
        </CategoriesItemsContainer>
      </CategoryListWrapper>
    </div>
  );
};
